import React from 'react'
import { connect } from 'react-redux'
import getData from '../../functions/getData'
import checkFSKExist from '../../functions/checkFSKExist'
import filterMoviesByWeekAndCinema from '../../functions/cinemaAndTimeRangeFilters/filterMoviesByWeekAndCinema'
import filterPerformancesByWeek from '../../functions/cinemaAndTimeRangeFilters/filterPerformancesByWeek'
import getCurrentCinemaWeeksArray from '../../functions/getCurrentCinemaWeeksArray'
import sortCinemas from '../../functions/sortCinemas'
import {
    PROGRAM_CLI_MOVIES_ITEMS,
    PROGRAM_CLI_MOVIES_ORDER,
    PROGRAM_PERFORMANCES,
    PROGRAM_CINEMAS,
    CLI_CINEMA_CLIENT_ID,
    CLI_CURRENT_CINEMA_ID,
    CLI_MULTICINEMA_MODE,
    FEATURES,
    AUTH,
} from '../../constants/state'
import {
    dashboardTogglePanel,
    dashboardToggleTheatre,
    dashboardSetCinemaFilter,
    changelogMarkRead,
    changelogMarkAllRead,
    dashboardIgnoreMovie,
    dashboardUnignoreMovie,
    dashboardSetWeeks,
} from '../../actions'
import PageDashboard from '../../components/PageDashboard'
import { CHANGELOG_ENTRIES } from '../../components/PageDashboard/changelogEntries'
import packageJson from '../../../package'
import config from '../../config.json'

const SOON_MS = 45 * 60 * 1000

const mapStateToProps = (state, ownProps) => {
    const moviesMap   = getData(state, PROGRAM_CLI_MOVIES_ITEMS)
    const moviesOrder = getData(state, PROGRAM_CLI_MOVIES_ORDER)
    const perfsMap    = getData(state, PROGRAM_PERFORMANCES)

    const order       = moviesOrder ? moviesOrder.toArray() : []
    const items       = moviesMap   ? moviesMap.toJS()      : {}
    const performances = perfsMap   ? perfsMap.toJS()       : {}
    const allPerfs    = Object.values(performances)

    // ── Cinema data (needed for multi-cinema gate + filter) ──
    const clientCinemaId = getData(state, CLI_CINEMA_CLIENT_ID)
    const cinemasIm       = getData(state, PROGRAM_CINEMAS)
    const cinemas         = cinemasIm ? cinemasIm.toJS() : {}

    const isMultiCinema = !!(cinemas[clientCinemaId] && cinemas[clientCinemaId].scope.type === 'multi')
    const cinemaIds     = isMultiCinema ? sortCinemas(clientCinemaId, cinemas) : []

    const currentCinemaId = getData(state, CLI_CURRENT_CINEMA_ID)
    const frontCinema     = cinemas[currentCinemaId]
    const defaultCinemaId = (frontCinema && frontCinema.scope.type === 'single')
        ? currentCinemaId
        : cinemaIds[0]

    const storedSelectedCinemaId = state.getIn(['ui', 'dashboard', 'selectedCinemaId'])
    const effectiveCinemaId = isMultiCinema ? (storedSelectedCinemaId || defaultCinemaId) : null

    // ── Cinema weeks (Thu–Wed), dashboard's own field, refreshed on mount ──
    const weeksIm = state.getIn(['ui', 'dashboard', 'weeks'])
    const weeks   = weeksIm ? weeksIm.toJS() : getCurrentCinemaWeeksArray()

    const weekObj     = weeks[1]
    const nextWeekObj = weeks[2]

    const nowMs = Date.now()

    // ── Filter movies into categories ─────────────────────
    const filtered = filterMoviesByWeekAndCinema(order, items, performances, weekObj, false)

    const currentMovieIds       = filtered.current
    const futureMovieIds        = filtered.future
    const noPerformancesMovieIds = filtered.noPerformances

    // ── Split future movies into next week vs later ───────
    const nextWeekFiltered = filterMoviesByWeekAndCinema(futureMovieIds, items, performances, nextWeekObj, false)
    const nextWeekMovieIds = nextWeekFiltered.current
    const laterMovieIds    = nextWeekFiltered.future

    // ── Performances this week ────────────────────────────
    const thisWeekPerformances = filterPerformancesByWeek(allPerfs, weekObj)

    // ── Incomplete movies (missing poster, trailer, logo, fsk, backgrounds, ambient, description, length) ─────
    const ignoredIdsIm  = state.getIn(['ui', 'dashboard', 'ignoredMovieIds'])
    const ignoredIdsSet = ignoredIdsIm ? new Set(ignoredIdsIm.toArray().map(String)) : new Set()

    const isMissingPoster      = m => !(m.images && m.images.poster && m.images.poster.url)
    const isMissingAmbient     = m => !(m.images && m.images.ambient && m.images.ambient.url)
    const isMissingBackgrounds = m => !(m.images && m.images.backgrounds && m.images.backgrounds.length > 0)

    const allIncompleteMovies = [...currentMovieIds, ...futureMovieIds]
        .map(id => items[id])
        .filter(m => m && (
            isMissingPoster(m) || !m.trailerURL || !m.logo || !checkFSKExist(m.fsk) ||
            isMissingBackgrounds(m) || isMissingAmbient(m) || !m.description || !m.length
        ))
        .map(m => ({
            id:                 m.id,
            title:              m.titleDisplay || m.title,
            posterUrl:          m.images && m.images.poster && m.images.poster.url || null,
            missingImage:       isMissingPoster(m),
            missingTrailer:     !m.trailerURL,
            missingLogo:        !m.logo,
            missingFsk:         !checkFSKExist(m.fsk),
            missingBackgrounds: isMissingBackgrounds(m),
            missingAmbient:     isMissingAmbient(m),
            missingDescription: !m.description,
            missingLength:      !m.length,
            editUrl:            `#${config.routes.movies.path}/movie/${m.id}/edit-item`,
        }))

    const activeIncompleteMovies  = allIncompleteMovies.filter(m => !ignoredIdsSet.has(String(m.id)))
    const ignoredIncompleteMovies = allIncompleteMovies.filter(m =>  ignoredIdsSet.has(String(m.id)))

    // ── Future movies panel ───────────────────────────────
    const buildFutureMovie = id => {
        const m = items[id]
        if (!m) return null

        const attrs = m.technologyAttributes || []
        const mp    = (m.performances || []).map(perfId => performances[perfId]).filter(Boolean)
        const firstFuturePerf = mp
            .filter(p => p.timeUtc && +p.timeUtc >= nowMs)
            .reduce((min, p) => (!min || +p.timeUtc < min ? +p.timeUtc : min), null)

        const rawDisplayDateStart =
            (firstFuturePerf ? new Date(firstFuturePerf).toISOString() : null) ||
            m.startingDate ||
            (m.limitations && m.limitations.visibleFrom)
        const displayDateStart = (typeof rawDisplayDateStart === 'string' || rawDisplayDateStart === null)
            ? rawDisplayDateStart
            : new Date(rawDisplayDateStart).toISOString()

        return {
            id:               m.id,
            title:            m.titleDisplay || m.title,
            displayDateStart,
            isPremiere:       attrs.some(a => a.id === 'premiere' || a.name === 'premiere'),
            isOV:             attrs.some(a => a.id === 'ov' || a.name === 'ov'),
            _sort:            displayDateStart ? new Date(displayDateStart).getTime() : Infinity,
        }
    }

    const sortBySort = (a, b) => a._sort - b._sort

    const nextWeekMovies = nextWeekMovieIds.map(buildFutureMovie).filter(Boolean).sort(sortBySort)
    const laterMovies    = laterMovieIds.map(buildFutureMovie).filter(Boolean).sort(sortBySort)

    // ── Current movies panel ─────────────────────────────
    const currentMoviesList = currentMovieIds
        .map(id => items[id])
        .filter(Boolean)
        .map(m => {
            const attrs = m.technologyAttributes || []
            return {
                id:         m.id,
                title:      m.titleDisplay || m.title,
                posterUrl:  m.images && m.images.poster && m.images.poster.url || null,
                isPremiere: attrs.some(a => a.id === 'premiere' || a.name === 'premiere'),
                isOV:       attrs.some(a => a.id === 'ov' || a.name === 'ov'),
                editUrl:    `#${config.routes.movies.path}/movie/${m.id}/edit-item`,
            }
        })

    // ── No performances panel ─────────────────────────────
    const noPerformancesMoviesList = noPerformancesMovieIds
        .map(id => items[id])
        .filter(Boolean)
        .map(m => ({
            id:        m.id,
            title:     m.titleDisplay || m.title,
            posterUrl: m.images && m.images.poster && m.images.poster.url || null,
            editUrl:   `#${config.routes.movies.path}/movie/${m.id}/edit-item`,
        }))

    // ── Today in cinema ───────────────────────────────────
    const now       = new Date()
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
    const todayEnd   = new Date(); todayEnd.setHours(23, 59, 59, 999)

    const todayPerfs = allPerfs.filter(p => {
        if (!p.timeUtc) return false
        if (effectiveCinemaId && p.cinemaId !== effectiveCinemaId) return false
        const d = new Date(+p.timeUtc)
        return d >= todayStart && d <= todayEnd
    })

    const theatreMap = {}
    todayPerfs.forEach(p => {
        const theatreId   = p.theatreName || p.theatreId || String(p.id)
        const theatreName = p.theatreName || String(p.theatreId) || 'Screen'
        const perfTime = new Date(+p.timeUtc)
        const movie    = moviesMap ? moviesMap.get(String(p.cliMoviePk)) : null
        const length   = movie ? movie.get('length') : null
        const endTime  = length ? new Date(perfTime.getTime() + length * 60000) : null
        const isNow    = endTime ? (now >= perfTime && now < endTime) : false
        const isPast   = endTime ? now >= endTime : perfTime < now
        const isSoon   = !isPast && !isNow && (perfTime - now) <= SOON_MS

        if (!theatreMap[theatreId]) {
            theatreMap[theatreId] = { id: theatreId, name: theatreName, screenings: [] }
        }
        theatreMap[theatreId].screenings.push({
            id:       p.id,
            time:     perfTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            film:     movie ? (movie.get('titleDisplay') || movie.get('title')) : String(p.cliMoviePk),
            isPast,
            isNow,
            isSoon,
            duration: length ? String(length) : null,
        })
    })

    const theatres = Object.values(theatreMap).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    theatres.forEach(theatre => {
        theatre.screenings.sort((a, b) => a.time.localeCompare(b.time))
        theatre.segments = theatre.screenings.map(s =>
            s.isPast ? 'done' : s.isNow ? 'now' : s.isSoon ? 'soon' : 'later'
        )
    })

    // ── Current date string ───────────────────────────────
    const currentDate = now.toLocaleDateString('de-DE', {
        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    }) + ' · ' + now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    const currentDateTimestamp = now.getTime()

    // ── UI state ─────────────────────────────────────────
    const openPanel    = state.getIn(['ui', 'dashboard', 'openPanel'])
    const openTheatres = state.getIn(['ui', 'dashboard', 'openTheatres'])

    // ── Caps ─────────────────────────────────────────────
    const capsIm = state.getIn([AUTH, 'caps'])
    const caps   = capsIm && capsIm.toJS ? capsIm.toJS() : capsIm || {}

    // ── Route enable/disable state ────────────────────────
    const routeDataIm      = state.getIn(['wpServerData', 'data', 'data', 'routes'])
    const routeData        = routeDataIm ? routeDataIm.toJS() : {}
    const webToPrintIm     = state.getIn(['wpServerData', 'data', 'data', 'webtoprint'])
    const webToPrintRoutes = webToPrintIm ? webToPrintIm.toJS() : {}

    if (caps.hasOwnProperty('eventDataGet')       && !caps.eventDataGet)       routeData.events                   = false
    if (caps.hasOwnProperty('pushDataGet')        && !caps.pushDataGet)        routeData.apps                     = false
    if (caps.hasOwnProperty('signageDataGet')     && !caps.signageDataGet)     { routeData.signage = false; routeData.signagelite = false }
    if (caps.hasOwnProperty('programDataSee')     && !caps.programDataSee)     { routeData.program = false; routeData.website = false; routeData.advertisement = false }
    if (caps.hasOwnProperty('socialMediaDataGet') && !caps.socialMediaDataGet) routeData.socialmediapostgenerator = false

    // ── Changelog ────────────────────────────────────────
    const readVersionsIm = state.getIn(['ui', 'dashboard', 'readVersions'])
    const readVersions   = readVersionsIm ? readVersionsIm.toArray() : []

    const cinemaProviderArr = []
    if (cinemas[clientCinemaId]) {
        if (cinemas[clientCinemaId].scope.type === 'multi') {
            cinemas[clientCinemaId].scope.cinemas.forEach(singleCinemaId => {
                const cinemaProvider = cinemas[singleCinemaId].scope.cinemaDataProvider.type
                if (!cinemaProviderArr.includes(cinemaProvider)) cinemaProviderArr.push(cinemaProvider)
            })
        } else if (cinemas[clientCinemaId].scope.type === 'single') {
            const cinemaProvider = cinemas[clientCinemaId].scope.cinemaDataProvider.type
            if (!cinemaProviderArr.includes(cinemaProvider)) cinemaProviderArr.push(cinemaProvider)
        }
    }

    return {
        currentWeek:             weekObj.isoWeek,
        currentMoviesCount:      currentMovieIds.length,
        futureMoviesCount:       futureMovieIds.length,
        noPerformancesCount:     noPerformancesMovieIds.length,
        performancesCount:       thisWeekPerformances.length,
        incompleteMovies:        activeIncompleteMovies,
        ignoredIncompleteMovies,
        currentMoviesList,
        noPerformancesMoviesList,
        nextWeekMovies,
        laterMovies,
        version:                 packageJson.version,
        theatres,
        todayPerformancesCount:  todayPerfs.length,
        todayMoviesCount:        [...new Set(todayPerfs.map(p => p.cliMoviePk))].length,
        cinemaFilterOptions:     cinemaIds.map(id => ({ id, nameShort: cinemas[id].nameShort })),
        selectedCinemaId:        effectiveCinemaId,
        currentDate,
        currentDateTimestamp,
        openPanel,
        openTheatres,
        changelogEntries:        CHANGELOG_ENTRIES,
        readVersions,
        cinemaProviderArr,
        routes:                  config.routes,
        routeData,
        multiCinemaMode:         getData(state, CLI_MULTICINEMA_MODE),
        hasNewsletter:           !!(webToPrintRoutes.newsletter),
        hasChatbot:              !!getData(state, [FEATURES, 'has_chatbot']),
        hasSocialMedia:          !!getData(state, [FEATURES, 'has_social_media_post_generator']),
        history:                 ownProps.history,
    }
}

const mapDispatchToProps = dispatch => ({
    onTogglePanel:   panelId   => dispatch(dashboardTogglePanel(panelId)),
    onToggleTheatre: theatreId => dispatch(dashboardToggleTheatre(theatreId)),
    onSelectCinema:  cinemaId  => dispatch(dashboardSetCinemaFilter(cinemaId)),
    onMarkRead:      version   => dispatch(changelogMarkRead(version)),
    onMarkAllRead:   versions  => dispatch(changelogMarkAllRead(versions)),
    onIgnoreMovie:   movieId   => dispatch(dashboardIgnoreMovie(movieId)),
    onUnignoreMovie: movieId   => dispatch(dashboardUnignoreMovie(movieId)),
    onSetWeeks:      weeks     => dispatch(dashboardSetWeeks(weeks)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PageDashboard)
